import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { jwtDecode } from 'jwt-decode';
import PinField from 'react-pin-field';
import cn from 'classnames';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomCheckbox from '../Components/OsloKommuneUI/OKomCheckbox';
import OKomCollapsible from '../Components/OsloKommuneUI/OKomCollapsible';
import OKomRadioGroup from '../Components/OsloKommuneUI/OKomRadioGroup';
import PktAlert from '../Components/PktAlert';
import { useUserContext } from '../Components/StateContext/UserContext';

const MAX_CHILDREN = 5;
const MAX_EVENTS_PER_CHILD = 3;

const DOB_REGEX = /^(0[1-9]|[12]\d|3[01])\.(0[1-9]|1[0-2])\.(\d{4})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NO_MOBILE_REGEX = /^[49]\d{7}$/;

type Lang = 'no' | 'ta';

type AthleticsChild = Api.AthleticsRegistrationChild;
type AthleticsForm = Api.AthleticsRegistrationRequest;

interface Strings {
  toggleNo: string;
  toggleTa: string;
  loading: string;
  childHeading: (n: number, total: number) => string;
  parentHeading: string;
  parent2Heading: string;
  firstname: string;
  lastname: string;
  firstnameTamil: string;
  lastnameTamil: string;
  gender: string;
  genderBoy: string;
  genderGirl: string;
  dateOfBirth: string;
  department: string;
  className: string;
  selectDepartment: string;
  selectClass: string;
  classLabel: (n: number) => string;
  parentEmail: string;
  parentMobile: string;
  addChild: string;
  addParent: string;
  removeChildAria: (n: number) => string;
  removeParent2Aria: string;
  send: string;
  validationTitle: string;
  childLabel: (n: number, total: number) => string;
  validation: {
    minOneChild: string;
    firstnameRequired: (label: string) => string;
    lastnameRequired: (label: string) => string;
    firstnameTamilRequired: (label: string) => string;
    lastnameTamilRequired: (label: string) => string;
    genderRequired: (label: string) => string;
    dobRequired: (label: string) => string;
    dobFormat: (label: string) => string;
    dobInvalid: (label: string) => string;
    departmentRequired: (label: string) => string;
    classRequired: (label: string) => string;
    parentEmailRequired: string;
    parentEmailInvalid: string;
    parentMobileRequired: string;
    parentMobileFormat: string;
    parent2EmailRequired: string;
    parent2EmailInvalid: string;
    parent2MobileRequired: string;
    parent2MobileFormat: string;
  };
  loadDepartmentsFailed: string;
  somethingWrong: string;
  successHeading: string;
  successLead: string;
  summaryHeading: string;
  summaryChildren: string;
  summaryParents: string;
  born: string;
  emailNotice: string;
  signoff: string;
  orgName: string;
  practicalHeading: string;
  practicalAddressLabel: string;
  practicalContactLabel: string;
  colFirstname: string;
  colLastname: string;
  colTamilName: string;
  colGender: string;
  colDob: string;
  colEmail: string;
  colMobile: string;
  privacyLink: string;
  pinHeading: string;
  alreadyHaveAccount: string;
  pinInstructions: (email: string) => string;
  pinJunkTitle: string;
  pinJunkBody: string;
  pinReset: string;
  loggedIn: string;
  eventsHeading: string;
  eventsHint: string;
  noEventsAvailable: string;
  loadEventsFailed: string;
  summaryEvents: string;
  colEvents: string;
  validationEventRequired: (label: string) => string;
  validationEventMax: (label: string) => string;
  matrixHelpLink: string;
  matrixAgeLabel: string;
  loginLink: string;
  alreadyRegisteredTitle: string;
  alreadyRegisteredBody: string;
  registrationClosedTitle: string;
  registrationClosedBody: string;
}

const stringsNo: Strings = {
  toggleNo: 'Norsk',
  toggleTa: 'தமிழ்',
  loading: 'Lagrer, vennligst vent...',
  childHeading: (n, total) => total > 1 ? `OM ELEV ${n}` : 'OM ELEVEN',
  parentHeading: 'OM FORELDER',
  parent2Heading: 'OM FORELDER 2',
  firstname: 'Fornavn',
  lastname: 'Etternavn',
  firstnameTamil: 'Fornavn (tamilsk)',
  lastnameTamil: 'Etternavn (tamilsk)',
  gender: 'Kjønn',
  genderBoy: 'Gutt',
  genderGirl: 'Jente',
  dateOfBirth: 'Fødselsdato (dd.mm.åååå)',
  department: 'Avdeling',
  className: 'Klasse',
  selectDepartment: '-- velg avdeling --',
  selectClass: '-- velg klasse --',
  classLabel: (n) => `${n}.klasse`,
  parentEmail: 'Forelder e-post',
  parentMobile: 'Forelder mobilnummer',
  addChild: '+ Legg til elev',
  addParent: '+ Legg til forelder',
  removeChildAria: (n) => `Fjern elev ${n}`,
  removeParent2Aria: 'Fjern forelder 2',
  send: 'Send',
  validationTitle: 'Mangler informasjon',
  childLabel: (n, total) => total > 1 ? `elev ${n}` : 'eleven',
  validation: {
    minOneChild: 'Minst en elev må registreres',
    firstnameRequired: (l) => `Fornavn er påkrevd for ${l}`,
    lastnameRequired: (l) => `Etternavn er påkrevd for ${l}`,
    firstnameTamilRequired: (l) => `Fornavn (tamilsk) er påkrevd for ${l}.`,
    lastnameTamilRequired: (l) => `Etternavn (tamilsk) er påkrevd for ${l}.!`,
    genderRequired: (l) => `Kjønn er påkrevd for ${l}`,
    dobRequired: (l) => `Fødselsdato er påkrevd for ${l}`,
    dobFormat: (l) => `Fødselsdato må være på format dd.mm.åååå for eksempel 25.03.2012 (${l})`,
    dobInvalid: (l) => `Fødselsdato finnes ikke (${l})`,
    departmentRequired: (l) => `Avdeling er påkrevd for ${l}`,
    classRequired: (l) => `Klasse er påkrevd for ${l}`,
    parentEmailRequired: 'Forelder e-post er påkrevd',
    parentEmailInvalid: 'Ugyldig e-postadresse',
    parentMobileRequired: 'Forelder mobilnummer er påkrevd',
    parentMobileFormat: 'Mobilnummer må være 8 siffer og starte med 4 eller 9 (uten +47)',
    parent2EmailRequired: 'Forelder 2 e-post er påkrevd',
    parent2EmailInvalid: 'Ugyldig e-postadresse for forelder 2',
    parent2MobileRequired: 'Forelder 2 mobilnummer er påkrevd',
    parent2MobileFormat: 'Mobilnummer for forelder 2 må være 8 siffer og starte med 4 eller 9 (uten +47)',
  },
  loadDepartmentsFailed: 'Kunne ikke laste avdelinger',
  somethingWrong: 'Noe gikk galt',
  successHeading: 'TAKK FOR PÅMELDINGEN',
  successLead: 'Vi har registrert påmeldingen din.',
  summaryHeading: 'Oppsummering av registrering:',
  summaryChildren: 'Elever:',
  summaryParents: 'Foreldre:',
  born: 'f.',
  emailNotice: 'Du vil også få en bekreftelse på epost.',
  signoff: 'Med vennlig hilsen',
  orgName: 'Annaipoopathi aktivitets- og kultursenter (APTAK)',
  practicalHeading: 'PRAKTISKE OPPLYSNINGER',
  practicalAddressLabel: 'Adresse',
  practicalContactLabel: 'Kontakt',
  colFirstname: 'Fornavn',
  colLastname: 'Etternavn',
  colTamilName: 'Tamilsk navn',
  colGender: 'Kjønn',
  colDob: 'Fødselsdato',
  colEmail: 'E-post',
  colMobile: 'Mobilnummer',
  privacyLink: 'Personvernvilkår',
  pinHeading: 'PIN-KODE',
  alreadyHaveAccount: 'Du har allerede en konto. Vi har sendt deg en pinkode på e-post.',
  pinInstructions: (email) => `Skriv inn pinkoden vi sendte til ${email}. Det kan ta opptil 2 minutter.`,
  pinJunkTitle: 'Finner du ikke pin-koden?',
  pinJunkBody: 'Gmail, Outlook, Hotmail — sjekk om e-posten har havnet i Junk Mail / Søppelpost.',
  pinReset: 'Logg inn på nytt',
  loggedIn: 'Du er nå innlogget',
  eventsHeading: 'Øvelser (maks 3)',
  eventsHint: 'Fyll inn fødselsdato for å se aktuelle øvelser',
  noEventsAvailable: 'Ingen øvelser tilgjengelig for denne aldersgruppen',
  loadEventsFailed: 'Kunne ikke laste øvelser',
  summaryEvents: 'Øvelser:',
  colEvents: 'Øvelser',
  validationEventRequired: (l) => `Velg minst én øvelse for ${l}`,
  validationEventMax: (l) => `Maks 3 øvelser kan velges for ${l}`,
  matrixHelpLink: 'Øvelsesoversikt',
  matrixAgeLabel: 'Alder',
  loginLink: 'Logg inn',
  alreadyRegisteredTitle: 'Påmelding allerede registrert',
  alreadyRegisteredBody: 'Vi har allerede registrert påmeldingen din. Send inn på nytt bare hvis du skal gjøre endringer.',
  registrationClosedTitle: 'Påmeldingen er stengt',
  registrationClosedBody: 'Påmeldingen til friidrett er stengt.',
};

const stringsTa: Strings = {
  toggleNo: 'Norsk',
  toggleTa: 'தமிழ்',
  loading: 'சேமிக்கப்படுகிறது, காத்திருக்கவும்...',
  childHeading: (n, total) => total > 1 ? `மாணவர் ${n} விவரம்` : 'மாணவர் விவரம்',
  parentHeading: 'பெற்றோர் விவரம்',
  parent2Heading: 'பெற்றோர் 2 விவரம்',
  firstname: 'பெயர் (நோர்வேயியமொழியில்)',
  lastname: 'குடும்பப்பெயர் (நோர்வேயியமொழியில்)',
  firstnameTamil: 'பெயர் (தமிழில்)',
  lastnameTamil: 'குடும்பப்பெயர் (தமிழில்)',
  gender: 'பால்',
  genderBoy: 'ஆண்',
  genderGirl: 'பெண்',
  dateOfBirth: 'பிறந்த திகதி',
  department: 'வளாகம்',
  className: 'கல்வி கற்கும் ஆண்டு',
  selectDepartment: '-- வளாகத்தைத் தேர்வு செய்யவும் --',
  selectClass: '-- கல்வி கற்கும் ஆண்டைத் தேர்வு செய்யவும் --',
  classLabel: (n) => `${n}ஆம் ஆண்டு`,
  parentEmail: 'மின்னஞ்சல் முகவரி',
  parentMobile: 'தொலைபேசி இலக்கம்',
  addChild: '+ மாணவரை இணைக்கவும்',
  addParent: '+ பெற்றோரை இணைக்கவும்',
  removeChildAria: (n) => `மாணவர் ${n} ஐ அகற்று`,
  removeParent2Aria: 'பெற்றோர் 2 ஐ அகற்று',
  send: 'அனுப்பவும்',
  validationTitle: 'தகவல் இல்லை',
  childLabel: (n, total) => total > 1 ? `மாணவர் ${n}` : 'மாணவர்',
  validation: {
    minOneChild: 'குறைந்தது ஒரு மாணவரைப் பதிவு செய்ய வேண்டும்',
    firstnameRequired: (l) => `${l}க்கு பெயர் தேவை`,
    lastnameRequired: (l) => `${l}க்கு குடும்பப்பெயர் தேவை`,
    firstnameTamilRequired: (l) => `${l}க்கு பெயர் (தமிழில்) தேவை`,
    lastnameTamilRequired: (l) => `${l}க்கு குடும்பப்பெயர் (தமிழில்) தேவை`,
    genderRequired: (l) => `${l}க்கு பால் தேவை`,
    dobRequired: (l) => `${l}க்கு பிறந்த திகதி தேவை`,
    dobFormat: (l) => `பிறந்த திகதி dd.mm.åååå வடிவில் இருக்க வேண்டும், எடுத்துக்காட்டாக 25.03.2012 (${l})`,
    dobInvalid: (l) => `பிறந்த திகதி தவறானது (${l})`,
    departmentRequired: (l) => `${l}க்கு வளாகம் தேவை`,
    classRequired: (l) => `${l}க்கு கல்வி கற்கும் ஆண்டு தேவை`,
    parentEmailRequired: 'மின்னஞ்சல் முகவரி தேவை',
    parentEmailInvalid: 'தவறான மின்னஞ்சல் முகவரி',
    parentMobileRequired: 'தொலைபேசி இலக்கம் தேவை',
    parentMobileFormat: 'தொலைபேசி இலக்கம் 8 இலக்கங்களாக இருக்க வேண்டும், 4 அல்லது 9 இல் தொடங்க வேண்டும் (+47 இல்லாமல்)',
    parent2EmailRequired: 'பெற்றோர் 2 இன் மின்னஞ்சல் முகவரி தேவை',
    parent2EmailInvalid: 'பெற்றோர் 2 க்கு தவறான மின்னஞ்சல் முகவரி',
    parent2MobileRequired: 'பெற்றோர் 2 இன் தொலைபேசி இலக்கம் தேவை',
    parent2MobileFormat: 'பெற்றோர் 2 இன் தொலைபேசி இலக்கம் 8 இலக்கங்களாக இருக்க வேண்டும், 4 அல்லது 9 இல் தொடங்க வேண்டும் (+47 இல்லாமல்)',
  },
  loadDepartmentsFailed: 'வளாகங்களை ஏற்ற முடியவில்லை',
  somethingWrong: 'ஏதோ தவறு நடந்தது',
  successHeading: 'பதிவுக்கு நன்றி',
  successLead: 'உங்கள் பதிவு பெறப்பட்டது.',
  summaryHeading: 'பதிவின் சுருக்கம்:',
  summaryChildren: 'குழந்தைகள்:',
  summaryParents: 'பெற்றோர்கள்:',
  born: 'பிறந்த திகதி:',
  emailNotice: 'உங்களுக்கு மின்னஞ்சலில் உறுதிப்படுத்தல் வரும்.',
  signoff: 'அன்பான வாழ்த்துகள்',
  orgName: 'Annaipoopathi aktivitets- og kultursenter (APTAK)',
  practicalHeading: 'நடைமுறை விவரங்கள்',
  practicalAddressLabel: 'முகவரி',
  practicalContactLabel: 'தொடர்புக்கு',
  colFirstname: 'பெயர்',
  colLastname: 'குடும்பப்பெயர்',
  colTamilName: 'தமிழ் பெயர்',
  colGender: 'பால்',
  colDob: 'பிறந்த திகதி',
  colEmail: 'மின்னஞ்சல்',
  colMobile: 'தொலைபேசி',
  privacyLink: 'தனிநபர் பாதுகாப்பு',
  pinHeading: 'பின் கோடு',
  alreadyHaveAccount: 'உங்களுக்கு ஏற்கனவே ஒரு கணக்கு உள்ளது. மின்னஞ்சலில் பின்கோடு அனுப்பப்பட்டது.',
  pinInstructions: (email) => `${email} க்கு அனுப்பிய பின்கோடை உள்ளிடுக. 2 நிமிடங்கள் வரை ஆகலாம்.`,
  pinJunkTitle: 'பின்கோடு கிடைக்கவில்லையா?',
  pinJunkBody: 'Gmail, Outlook, Hotmail — மின்னஞ்சல் ஜங்க்/ஸ்பேம் (Junk/Spam) பகுதியில் உள்ளதா எனப் பாருங்கள்.',
  pinReset: 'மீண்டும் முயற்சிக்க',
  loggedIn: 'நீங்கள் இப்போது உள்நுழைந்துள்ளீர்கள்',
  eventsHeading: 'விளையாட்டுகள் (அதிகபட்சம் 3)',
  eventsHint: 'விளையாட்டுகளைப் பார்க்க பிறந்த திகதியை உள்ளிடுக',
  noEventsAvailable: 'இந்த வயதிற்கு விளையாட்டுகள் இல்லை',
  loadEventsFailed: 'விளையாட்டுகளை ஏற்ற முடியவில்லை',
  summaryEvents: 'விளையாட்டுகள்:',
  colEvents: 'விளையாட்டுகள்',
  validationEventRequired: (l) => `${l}க்கு குறைந்தது ஒரு விளையாட்டை தேர்ந்தெடுக்கவும்`,
  validationEventMax: (l) => `${l}க்கு அதிகபட்சம் 3 விளையாட்டுகள் மட்டுமே`,
  matrixHelpLink: 'விளையாட்டுப் பட்டியல்',
  matrixAgeLabel: 'வயது',
  loginLink: 'உள்நுழைய',
  alreadyRegisteredTitle: 'பதிவு ஏற்கனவே செய்யப்பட்டுள்ளது',
  alreadyRegisteredBody: 'உங்கள் பதிவு ஏற்கனவே செய்யப்பட்டுள்ளது. மாற்றங்கள் செய்ய விரும்பினால் மட்டுமே மீண்டும் சமர்ப்பிக்கவும்.',
  registrationClosedTitle: 'பதிவு மூடப்பட்டுள்ளது',
  registrationClosedBody: 'மெய்வல்லுநர் போட்டிக்கான பதிவு தற்போது மூடப்பட்டுள்ளது.',
};

const STRINGS: Record<Lang, Strings> = { no: stringsNo, ta: stringsTa };

const EXCLUDED_DEPARTMENT_IDS = new Set<number>([5, 7]);
const EXCLUDED_EVENT_READABLE_IDS = new Set<string>(['stafett_100_x_4', 'kulai_eduthal']);

// Module-scope so the reference is stable across renders. react-pin-field v4
// re-runs an internal effect whenever `format` changes identity, which would
// otherwise fire onChange/onComplete in a tight loop.
const formatPinDigit = (k: string): string => (/^\d$/.test(k) ? k : '');

const PRACTICAL_INFO = {
  addressLines: ['Stovnerbanen', 'Smiuvegen 253', '0981 Oslo'],
  contactEmail: 'kontakt@annaipoopathi.no',
  contactPhone: '+47 972 97 778',
};
const MAPS_URL = `https://www.google.com/maps?q=${encodeURIComponent(PRACTICAL_INFO.addressLines.join(', '))}`;

interface PracticalEvent {
  name: Record<Lang, string>;
  date: Record<Lang, string>;
}

const PRACTICAL_EVENTS: PracticalEvent[] = [
  {
    name: {
      no: 'Friidrettsdag',
      ta: 'இல்ல மெய்வல்லுநர் போட்டி நாள்',
    },
    date: {
      no: 'lørdag 13.06.2026 kl. 09:00',
      ta: 'சனிக்கிழமை 13.06.2026 காலை 09:00',
    },
  },
];

const emptyChild = (): AthleticsChild => ({
  Firstname: '',
  Lastname: '',
  FirstnameTamil: '',
  LastnameTamil: '',
  DateOfBirth: '',
  DepartmentId: 0,
  EventIds: [],
  Gender: '',
});

const emptyForm = (): AthleticsForm => ({
  Children: [emptyChild()],
  ParentEmail: '',
  ParentMobile: '',
  Parent2Email: '',
  Parent2Mobile: '',
});

const parseBirthYear = (dob?: string): number | null => {
  if (!dob) return null;
  const m = dob.trim().match(/^\d{2}\.\d{2}\.(\d{4})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  return Number.isFinite(year) && year > 1900 ? year : null;
};

const ageFromDob = (dob?: string): number | null => {
  const year = parseBirthYear(dob);
  return year == null ? null : new Date().getFullYear() - year;
};

const formatDob = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
};

const summaryTableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
  width: '100%',
};
const summaryThStyle: React.CSSProperties = {
  border: '1px solid #ccc',
  padding: '6px 10px',
  background: '#f0f0f0',
  textAlign: 'left',
};
const summaryTdStyle: React.CSSProperties = {
  border: '1px solid #ccc',
  padding: '6px 10px',
};

const removeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '1.5rem',
  lineHeight: 1,
  padding: '0 .25rem',
};

const LanguageToggle: React.FC<{ language: Lang; setLanguage: (l: Lang) => void; t: Strings }> = ({ language, setLanguage, t }) => (
  <div className="ods-grid__column--12 ods-text--align-center">
    <div role="group" aria-label="Language" style={{ display: 'inline-flex', gap: '.5rem' }}>
      <button
        type="button"
        onClick={() => setLanguage('no')}
        className={language === 'no' ? 'ods-button' : 'ods-button ods-button--outline'}
      >{t.toggleNo}</button>
      <button
        type="button"
        onClick={() => setLanguage('ta')}
        className={language === 'ta' ? 'ods-button' : 'ods-button ods-button--outline'}
      >{t.toggleTa}</button>
    </div>
  </div>
);

const AthleticsRegistrationView: React.FC<RouteComponentProps> = () => {
  const { user, userToken, setToken, setUserInfo, setDepartmentInfo } = useUserContext();
  const [language, setLanguage] = useState<Lang>('no');
  const t = STRINGS[language];

  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState<Api.Department[]>([]);
  const [events, setEvents] = useState<Api.AthleticsEventDto[]>([]);
  const [form, setForm] = useState<AthleticsForm>(emptyForm());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showParent2, setShowParent2] = useState(false);
  const [submittedData, setSubmittedData] = useState<AthleticsForm | null>(null);
  const [accountExistsForEmail, setAccountExistsForEmail] = useState<string | null>(null);
  const [emailExistsFor, setEmailExistsFor] = useState<string | null>(null);
  const [lastCheckedEmail, setLastCheckedEmail] = useState<string | null>(null);
  const [pinCompleted, setPinCompleted] = useState(false);
  const [hasExistingRegistration, setHasExistingRegistration] = useState(false);
  // null = not yet known; false = closed (show banner, block submit).
  const [registrationOpen, setRegistrationOpen] = useState<boolean | null>(null);
  // Tracks in-flight AI name translations, keyed by `${childIndex}-${targetField}`.
  const [translating, setTranslating] = useState<Record<string, boolean>>({});
  const isAuthenticated = !!userToken;

  const validateChild = (child: AthleticsChild, label: string): string | null => {
    if (!child.Firstname.trim()) return t.validation.firstnameRequired(label);
    if (!child.Lastname.trim()) return t.validation.lastnameRequired(label);
    if (!child.FirstnameTamil?.trim()) return t.validation.firstnameTamilRequired(label);
    if (!child.LastnameTamil?.trim()) return t.validation.lastnameTamilRequired(label);
    if (child.Gender !== 'boy' && child.Gender !== 'girl') return t.validation.genderRequired(label);
    if (!child.DateOfBirth?.trim()) return t.validation.dobRequired(label);
    if (!DOB_REGEX.test(child.DateOfBirth.trim())) return t.validation.dobFormat(label);
    const [day, month, year] = child.DateOfBirth.trim().split('.').map(Number);
    const dob = new Date(year, month - 1, day);
    if (dob.getFullYear() !== year || dob.getMonth() !== month - 1 || dob.getDate() !== day) return t.validation.dobInvalid(label);
    if (!child.DepartmentId || child.DepartmentId <= 0) return t.validation.departmentRequired(label);
    if (child.EventIds.length === 0) return t.validationEventRequired(label);
    if (child.EventIds.length > MAX_EVENTS_PER_CHILD) return t.validationEventMax(label);
    return null;
  };

  const validateForm = (f: AthleticsForm): string | null => {
    if (!f.Children || f.Children.length === 0) return t.validation.minOneChild;
    for (let i = 0; i < f.Children.length; i++) {
      const err = validateChild(f.Children[i], t.childLabel(i + 1, f.Children.length));
      if (err) return err;
    }
    if (!f.ParentEmail.trim()) return t.validation.parentEmailRequired;
    if (!EMAIL_REGEX.test(f.ParentEmail.trim())) return t.validation.parentEmailInvalid;
    if (!f.ParentMobile?.trim()) return t.validation.parentMobileRequired;
    if (!NO_MOBILE_REGEX.test(f.ParentMobile.trim())) return t.validation.parentMobileFormat;
    const hasParent2 = !!(f.Parent2Email?.trim() || f.Parent2Mobile?.trim());
    if (hasParent2) {
      if (!f.Parent2Email?.trim()) return t.validation.parent2EmailRequired;
      if (!EMAIL_REGEX.test(f.Parent2Email.trim())) return t.validation.parent2EmailInvalid;
      if (!f.Parent2Mobile?.trim()) return t.validation.parent2MobileRequired;
      if (!NO_MOBILE_REGEX.test(f.Parent2Mobile.trim())) return t.validation.parent2MobileFormat;
    }
    return null;
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await RestService.get<Api.Department[]>('/api/examregistration/GetDepartments');
        setDepartments(res);
      } catch (e) {
        toast.error(t.loadDepartmentsFailed);
      }
    })();
    (async () => {
      try {
        const res = await RestService.get<Api.AthleticsEventDto[]>(`/api/athleticsregistration/GetEvents?year=${new Date().getFullYear()}`);
        setEvents(res);
      } catch (e) {
        toast.error(t.loadEventsFailed);
      }
    })();
    (async () => {
      try {
        const w = await RestService.get<Api.EventsAdministrationDto>('/api/eventsadministration/GetRegistrationWindow?eventType=athletics');
        setRegistrationOpen(!!w.RegistrationIsOpen);
      } catch {
        // If the check fails, fall back to closed so we never accept submissions the server would reject.
        setRegistrationOpen(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm(prev => ({
      ...prev,
      ParentEmail: prev.ParentEmail?.trim() ? prev.ParentEmail : (user.Email ?? ''),
      ParentMobile: prev.ParentMobile?.trim() ? prev.ParentMobile : (user.Mobilephone ?? ''),
    }));
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const rows = await RestService.get<Api.AthleticsRegistrationDto[]>('/api/athleticsregistration/MyRegistrations');
        if (rows && rows.length > 0) {
          const first = rows[0];
          setForm({
            Children: rows.map(r => ({
              Firstname: r.Firstname ?? '',
              Lastname: r.Lastname ?? '',
              FirstnameTamil: r.FirstnameTamil ?? '',
              LastnameTamil: r.LastnameTamil ?? '',
              DateOfBirth: formatDob(r.DateOfBirth),
              DepartmentId: r.DepartmentId ?? 0,
              EventIds: r.EventIds ?? [],
              Gender: r.Gender ?? '',
            })),
            ParentEmail: first.Parent1Email ?? '',
            ParentMobile: first.Parent1Mobile ?? '',
            Parent2Email: first.Parent2Email ?? '',
            Parent2Mobile: first.Parent2Mobile ?? '',
          });
          setShowParent2(!!first.Parent2Email);
          setHasExistingRegistration(true);
        }
      } catch {
        // user has no existing registrations or fetch failed — silently leave form blank
      }
    })();
  }, [isAuthenticated]);

  const updateParent = <K extends keyof AthleticsForm>(field: K, value: AthleticsForm[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setValidationError(null);
    setEmailExistsFor(null);
  };

  const updateChild = <K extends keyof AthleticsChild>(index: number, field: K, value: AthleticsChild[K]) => {
    setForm(prev => ({
      ...prev,
      Children: prev.Children.map((c, i) => {
        if (i !== index) return c;
        const updated = { ...c, [field]: value };
        if (field === 'DateOfBirth') updated.EventIds = [];
        return updated;
      }),
    }));
    setValidationError(null);
    setEmailExistsFor(null);
  };

  const toggleEvent = (childIndex: number, eventId: number) => {
    setForm(prev => ({
      ...prev,
      Children: prev.Children.map((c, i) => {
        if (i !== childIndex) return c;
        if (c.EventIds.includes(eventId)) {
          return { ...c, EventIds: c.EventIds.filter(id => id !== eventId) };
        }
        const trimmed = c.EventIds.length >= MAX_EVENTS_PER_CHILD
          ? c.EventIds.slice(0, MAX_EVENTS_PER_CHILD - 1)
          : c.EventIds;
        return { ...c, EventIds: [...trimmed, eventId] };
      }),
    }));
    setValidationError(null);
  };

  const eligibleEventsFor = (child: AthleticsChild): Api.AthleticsEventDto[] => {
    const age = ageFromDob(child.DateOfBirth);
    if (age == null) return [];
    return events.filter(e => e.Ages.includes(age) && !EXCLUDED_EVENT_READABLE_IDS.has(e.ReadableId));
  };


  const addChild = () => {
    setForm(prev => {
      if (prev.Children.length >= MAX_CHILDREN) return prev;
      const next = emptyChild();
      const firstDept = prev.Children[0]?.DepartmentId;
      if (firstDept && firstDept > 0) next.DepartmentId = firstDept;
      return { ...prev, Children: [...prev.Children, next] };
    });
    setValidationError(null);
  };

  const removeChild = (index: number) => {
    setForm(prev => ({ ...prev, Children: prev.Children.filter((_, i) => i !== index) }));
    setValidationError(null);
  };

  const addParent2 = () => setShowParent2(true);
  const removeParent2 = () => {
    setShowParent2(false);
    setForm(prev => ({ ...prev, Parent2Email: '', Parent2Mobile: '' }));
    setValidationError(null);
  };

  const checkAccountAndSendPin = async () => {
    if (userToken) return;
    const email = form.ParentEmail.trim().toLowerCase();
    if (!email || !EMAIL_REGEX.test(email)) return;
    if (email === lastCheckedEmail) return;
    setLastCheckedEmail(email);
    try {
      const response = await RestService.get<Api.LoginResponse>(`/api/login/sendpin?email=${encodeURIComponent(email)}`);
      if (response.Success) {
        setAccountExistsForEmail(email);
        setPinCompleted(false);
        if (response.Message != null) {
          toast.success(response.Message);
        }
      }
    } catch {
      // best-effort; if check fails just let the user continue with normal registration
    }
  };

  const handlePinCode = async (pin: string) => {
    if (!accountExistsForEmail) return;
    try {
      setIsLoading(true);
      const response = await RestService.get<Api.LoginResponse>(
        `/api/login/login?email=${encodeURIComponent(accountExistsForEmail)}&pinCode=${pin}`,
      );
      if (response.Success) {
        setToken(response.Token);
        const userId = (jwtDecode(response.Token) as any)['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
        const me = await RestService.get<Api.User>(`/api/user/getuser/${userId}`);
        setUserInfo(me);
        if (me.DepartmentId) {
          const dep = await RestService.get<Api.Department>(`/api/department/GetDepartment?id=${me.DepartmentId}`);
          setDepartmentInfo(dep);
        }
        if (me.Mobilephone) {
          setForm(prev => ({ ...prev, ParentMobile: prev.ParentMobile?.trim() ? prev.ParentMobile : me.Mobilephone }));
        }
        setAccountExistsForEmail(null);
        setPinCompleted(false);
        toast.success(t.loggedIn);
      } else {
        setPinCompleted(false);
        toast.error(`${response.Message}`);
      }
    } catch (e) {
      toast.error(`${t.somethingWrong}: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  // PinField v4 re-fires its onChange/onComplete effect whenever the callback
  // identity changes. Pin-down a stable callback via a ref so the loop doesn't
  // run away (entering 5 digits would otherwise hit /api/login/login many times).
  const handlePinCodeRef = useRef(handlePinCode);
  handlePinCodeRef.current = handlePinCode;

  const handlePinComplete = useCallback((pin: string) => {
    setPinCompleted(true);
    handlePinCodeRef.current(pin);
  }, []);

  const pinJunkRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!accountExistsForEmail) return;
    if (!pinJunkRef.current) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(min-width: 768px)').matches) return;
    pinJunkRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [accountExistsForEmail]);

  const resetPinForm = () => {
    setAccountExistsForEmail(null);
    setPinCompleted(false);
    setLastCheckedEmail(null);
  };

  const translateNameField = async (
    index: number,
    sourceField: 'Firstname' | 'Lastname',
    targetField: 'FirstnameTamil' | 'LastnameTamil',
  ) => {
    const child = form.Children[index];
    if (!child) return;
    const english = (child[sourceField] ?? '').trim();
    if (!english) return;
    if ((child[targetField] ?? '').trim()) return;
    const key = `${index}-${targetField}`;
    setTranslating(prev => ({ ...prev, [key]: true }));
    try {
      const res = await RestService.post<Api.TranslateNameRequest, Api.TranslateNameResponse>(
        '/api/examregistration/TranslateName',
        { EnglishName: english },
      );
      if (res?.Tamil) {
        setForm(prev => ({
          ...prev,
          Children: prev.Children.map((c, i) =>
            i === index && !(c[targetField] ?? '').trim() ? { ...c, [targetField]: res.Tamil } : c,
          ),
        }));
      }
    } catch {
      // best-effort; user can type manually
    } finally {
      setTranslating(prev => ({ ...prev, [key]: false }));
    }
  };

  const detectGenderField = async (index: number) => {
    const child = form.Children[index];
    if (!child) return;
    const firstname = (child.Firstname ?? '').trim();
    if (!firstname) return;
    // Don't overwrite a gender the user (or a prior detection) has already set.
    if (child.Gender === 'boy' || child.Gender === 'girl') return;
    const key = `${index}-Gender`;
    setTranslating(prev => ({ ...prev, [key]: true }));
    try {
      const res = await RestService.post<Api.DetectGenderRequest, Api.DetectGenderResponse>(
        '/api/examregistration/DetectGender',
        { FirstName: firstname },
      );
      const detected = res?.Gender;
      if (detected === 'boy' || detected === 'girl') {
        setForm(prev => ({
          ...prev,
          Children: prev.Children.map((c, i) =>
            i === index && c.Gender !== 'boy' && c.Gender !== 'girl' ? { ...c, Gender: detected } : c,
          ),
        }));
      }
    } catch {
      // best-effort; user can pick manually
    } finally {
      setTranslating(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleFirstnameBlur = (index: number) => {
    translateNameField(index, 'Firstname', 'FirstnameTamil');
    detectGenderField(index);
  };

  const handleSubmit = async () => {
    if (registrationOpen !== true) {
      setValidationError(t.registrationClosedBody);
      return;
    }
    const error = validateForm(form);
    if (error) {
      setValidationError(error);
      setEmailExistsFor(null);
      return;
    }
    setValidationError(null);
    setEmailExistsFor(null);
    try {
      setIsLoading(true);
      const endpoint = isAuthenticated
        ? '/api/athleticsregistration/UpdateMyRegistrations'
        : '/api/athleticsregistration/Register';
      const response = await RestService.post<Api.AthleticsRegistrationRequest, Api.RegisterAthleticsResponse>(endpoint, form);
      if (response.Success) {
        const snapshot: AthleticsForm = JSON.parse(JSON.stringify(form));
        setSubmittedData(snapshot);
        setForm(emptyForm());
        setShowParent2(false);
      } else if (response.EmailAlreadyExists) {
        setEmailExistsFor(form.ParentEmail.trim());
        setValidationError(response.Message ?? t.somethingWrong);
      } else {
        setValidationError(response.Message ?? t.somethingWrong);
      }
    } catch (e) {
      toast.error(`${t.somethingWrong}: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (submittedData) {
    const departmentName = (id?: number) => departments.find(d => d.Id === id)?.Name ?? '';
    return (
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <LanguageToggle language={language} setLanguage={setLanguage} t={t} />
        <div className="ods-grid__column--12 ods-grid__column--start-3-breakpoint-medium ods-grid__column--end-11-breakpoint-medium">
          <div className="ods-status-card">
            <div className="ods-status-card__content">
              <h3 className="ods-status-card__heading">{t.successHeading}</h3>
              <p>{t.successLead}</p>

              <h4 className="ods-margin-top-11">{t.summaryHeading}</h4>

              <p className="ods-margin-top-3"><strong>{t.summaryChildren}</strong></p>
              <table className="ods-margin-top-2" style={summaryTableStyle}>
                <thead>
                  <tr>
                    <th style={summaryThStyle}>{t.colFirstname}</th>
                    <th style={summaryThStyle}>{t.colLastname}</th>
                    <th style={summaryThStyle}>{t.colTamilName}</th>
                    <th style={summaryThStyle}>{t.colGender}</th>
                    <th style={summaryThStyle}>{t.colDob}</th>
                    <th style={summaryThStyle}>{t.colEvents}</th>
                    <th style={summaryThStyle}>{t.department}</th>
                  </tr>
                </thead>
                <tbody>
                  {submittedData.Children.map((c, i) => {
                    const eventNames = c.EventIds
                      .map(id => events.find(e => e.Id === id))
                      .filter((e): e is Api.AthleticsEventDto => !!e)
                      .map(e => language === 'no' ? e.NameNo : e.NameTa)
                      .join(', ');
                    return (
                      <tr key={i}>
                        <td style={summaryTdStyle}>{c.Firstname}</td>
                        <td style={summaryTdStyle}>{c.Lastname}</td>
                        <td style={summaryTdStyle}>{[c.FirstnameTamil, c.LastnameTamil].filter(Boolean).join(' ')}</td>
                        <td style={summaryTdStyle}>{c.Gender === 'boy' ? t.genderBoy : c.Gender === 'girl' ? t.genderGirl : ''}</td>
                        <td style={summaryTdStyle}>{c.DateOfBirth}</td>
                        <td style={summaryTdStyle}>{eventNames}</td>
                        <td style={summaryTdStyle}>{departmentName(c.DepartmentId)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <p className="ods-margin-top-3"><strong>{t.summaryParents}</strong></p>
              <table className="ods-margin-top-2" style={summaryTableStyle}>
                <thead>
                  <tr>
                    <th style={summaryThStyle}>{t.colEmail}</th>
                    <th style={summaryThStyle}>{t.colMobile}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={summaryTdStyle}>{submittedData.ParentEmail}</td>
                    <td style={summaryTdStyle}>{submittedData.ParentMobile}</td>
                  </tr>
                  {submittedData.Parent2Email ? (
                    <tr>
                      <td style={summaryTdStyle}>{submittedData.Parent2Email}</td>
                      <td style={summaryTdStyle}>{submittedData.Parent2Mobile}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>

              <p className="ods-margin-top-11">{t.emailNotice}</p>

              <p className="ods-margin-top-11">
                {t.signoff}<br />
                {t.orgName}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isLoading && <OKomLoading text={t.loading} />}
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '95rem' }}>
        <LanguageToggle language={language} setLanguage={setLanguage} t={t} />

        {registrationOpen === false && (
          <div className="ods-grid__column--12">
            <aside className="ods-infobox ods-infobox--yellow">
              <h2 className="ods-infobox__heading">{t.registrationClosedTitle}</h2>
              <div className="ods-content">
                <p>{t.registrationClosedBody}</p>
              </div>
            </aside>
          </div>
        )}

        {hasExistingRegistration && (
          <div className="ods-grid__column--12">
            <aside className="ods-infobox ods-infobox--yellow">
              <h2 className="ods-infobox__heading">{t.alreadyRegisteredTitle}</h2>
              <div className="ods-content">
                <p>{t.alreadyRegisteredBody}</p>
              </div>
            </aside>
          </div>
        )}

        <div className="ods-grid__column--12 ods-grid__column--start-1-breakpoint-medium ods-grid__column--end-5-breakpoint-medium">
          <div className="ods-status-card ods-status-card--mobile-bare">
            <div className="ods-status-card__content">
              <OKomCollapsible heading={t.practicalHeading} mobileOnly id="athletics-practical-info">

                {PRACTICAL_EVENTS.map((ev, i) => (
                  <div key={i} className="ods-margin-top-2"><br />
                    <div>{ev.name[language]}</div>
                    <div><strong>{ev.date[language]}</strong></div>
                  </div>
                ))}
                <br />
                <p className="ods-margin-top-4">
                  <strong>{t.practicalContactLabel}:</strong><br />
                  <a href={`mailto:${PRACTICAL_INFO.contactEmail}`} className="o-kom-link">{PRACTICAL_INFO.contactEmail}</a><br />
                  <a href={`tel:${PRACTICAL_INFO.contactPhone.replace(/\s+/g, '')}`} className="o-kom-link">{PRACTICAL_INFO.contactPhone}</a>
                </p>
                <p className="ods-margin-top-2">
                  <strong>{t.practicalAddressLabel}:</strong><br />
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="o-kom-link">
                    {PRACTICAL_INFO.addressLines.map((line, i) => (
                      <React.Fragment key={i}>{line}{i < PRACTICAL_INFO.addressLines.length - 1 && <br />}</React.Fragment>
                    ))}
                    <span className="ods-icon ods-icon--new-window ods-margin-left-1" aria-hidden="true"></span>
                  </a>
                </p>
                <a href="/assets/stovnerbanen.jpg" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block' }}>
                  <img
                    src="/assets/stovnerbanen.jpg"
                    alt={PRACTICAL_INFO.addressLines[0]}
                    style={{ width: '100%', maxWidth: 240, height: 'auto', borderRadius: 4, border: '1px solid #ccc' }}
                  />
                </a>
              </OKomCollapsible>
            </div>
          </div>
        </div>

        <div className="ods-grid__column--12 ods-grid__column--start-5-breakpoint-medium ods-grid__column--end-9-breakpoint-medium">
          {accountExistsForEmail ? (
            <>
              <aside ref={pinJunkRef} className="ods-infobox ods-infobox--yellow ods-margin-bottom-3">
                <h2 className="ods-infobox__heading">{t.pinJunkTitle}</h2>
                <div className="ods-content">
                  <p>{t.pinJunkBody}</p>
                </div>
              </aside>
              <div className="ods-status-card">
                <div className="ods-status-card__content">
                  <h3 className="ods-status-card__heading">{t.pinHeading}</h3>
                  <p className="ods-margin-top-2">{t.alreadyHaveAccount}</p>
                  <p className="ods-margin-top-2">{t.pinInstructions(accountExistsForEmail)}</p>
                  <div className="pin-field-container">
                    <PinField
                      length={5}
                      inputMode="numeric"
                      className={cn('pin-field', { complete: pinCompleted })}
                      onComplete={handlePinComplete}
                      format={formatPinDigit}
                      autoFocus
                      disabled={pinCompleted}
                      autoComplete="one-time-code"
                    />
                  </div>
                  <div className="ods-status-card__actions">
                    <div className="ods-text--align-center">
                      <button disabled={isLoading} className="ods-button ods-button--outline" onClick={resetPinForm}>
                        <span className="ods-button__icon ods-button__icon--right ods-icon--chevron-thin-left"></span> {t.pinReset}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="ods-status-card">
              <div className="ods-status-card__content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="ods-status-card__heading">{t.parentHeading}</h3>
                </div>

                <label className="ods-input__label">{t.parentEmail}
                  <input value={form.ParentEmail} onChange={e => updateParent('ParentEmail', e.target.value)} onBlur={checkAccountAndSendPin} disabled={!!userToken} style={{ backgroundColor: userToken ? '#dadada' : '' }} className="ods-input__input" type="email" autoComplete="email" />
                </label>

                <label className="ods-input__label">{t.parentMobile}
                  <input value={form.ParentMobile ?? ''} onChange={e => updateParent('ParentMobile', e.target.value)} disabled={!!userToken} style={{ backgroundColor: userToken ? '#dadada' : '' }} className="ods-input__input" type="tel" autoComplete="tel" />
                </label>
              </div>
            </div>
          )}

          {!accountExistsForEmail && (showParent2 ? (
            <div className="ods-status-card ods-margin-top-3">
              <div className="ods-status-card__content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="ods-status-card__heading">{t.parent2Heading}</h3>
                  <button type="button" onClick={removeParent2} aria-label={t.removeParent2Aria} style={removeButtonStyle}>×</button>
                </div>

                <label className="ods-input__label">{t.parentEmail}
                  <input value={form.Parent2Email ?? ''} onChange={e => updateParent('Parent2Email', e.target.value)} className="ods-input__input" type="email" autoComplete="off" />
                </label>

                <label className="ods-input__label">{t.parentMobile}
                  <input value={form.Parent2Mobile ?? ''} onChange={e => updateParent('Parent2Mobile', e.target.value)} className="ods-input__input" type="tel" autoComplete="off" />
                </label>
              </div>
            </div>
          ) : (
            <button type="button" onClick={addParent2} className="ods-button ods-button--yellow ods-button--fullwidth ods-margin-top-3">{t.addParent}</button>
          ))}
        </div>

        <div className="ods-grid__column--12 ods-grid__column--start-9-breakpoint-medium ods-grid__column--end-13-breakpoint-medium">
          {form.Children.map((child, index) => (
            <div key={index} className={index === 0 ? 'ods-status-card' : 'ods-status-card ods-margin-top-3'}>
              <div className="ods-status-card__content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 className="ods-status-card__heading">{t.childHeading(index + 1, form.Children.length)}</h3>
                  {index > 0 && (
                    <button type="button" onClick={() => removeChild(index)} aria-label={t.removeChildAria(index + 1)} style={removeButtonStyle}>×</button>
                  )}
                </div>

                <label className="ods-input__label">{t.firstname}
                  <input value={child.Firstname} onChange={e => updateChild(index, 'Firstname', e.target.value)} onBlur={() => handleFirstnameBlur(index)} className="ods-input__input" type="text" autoComplete="off" />
                </label>

                <label className="ods-input__label">{t.lastname}
                  <input value={child.Lastname} onChange={e => updateChild(index, 'Lastname', e.target.value)} onBlur={() => translateNameField(index, 'Lastname', 'LastnameTamil')} className="ods-input__input" type="text" autoComplete="off" />
                </label>

                <label className="ods-input__label ods-margin-top-11">{t.firstnameTamil}
                  {translating[`${index}-FirstnameTamil`] && (
                    <span className="ods-margin-left-1" style={{ display: 'inline-block', verticalAlign: 'middle' }}><BeatLoader color="#2a2859" size={8} /></span>
                  )}
                  <input value={child.FirstnameTamil ?? ''} onChange={e => updateChild(index, 'FirstnameTamil', e.target.value)} className="ods-input__input" type="text" />
                </label>

                <label className="ods-input__label">{t.lastnameTamil}
                  {translating[`${index}-LastnameTamil`] && (
                    <span className="ods-margin-left-1" style={{ display: 'inline-block', verticalAlign: 'middle' }}><BeatLoader color="#2a2859" size={8} /></span>
                  )}
                  <input value={child.LastnameTamil ?? ''} onChange={e => updateChild(index, 'LastnameTamil', e.target.value)} className="ods-input__input" type="text" />
                </label>

                <div className="ods-margin-top-11">
                  <OKomRadioGroup
                    legend={t.gender}
                    name={`gender-${index}`}
                    value={child.Gender ?? ''}
                    onChange={v => updateChild(index, 'Gender', v)}
                    loading={!!translating[`${index}-Gender`]}
                    options={[
                      { value: 'boy',  label: t.genderBoy },
                      { value: 'girl', label: t.genderGirl },
                    ]}
                  />
                </div>

                <label className="ods-input__label ods-margin-top-11">{t.dateOfBirth}
                  <input value={child.DateOfBirth ?? ''} onChange={e => updateChild(index, 'DateOfBirth', e.target.value)} className="ods-input__input" type="text" placeholder="dd.mm.åååå" />
                </label>

                {(() => {
                  const age = ageFromDob(child.DateOfBirth);
                  const eligible = eligibleEventsFor(child);
                  return (
                    <>
                      <label className="ods-input__label">
                        {t.eventsHeading}
                        {' '}
                        <a
                          href="/athleticsregistration/matrix"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="o-kom-link"
                          style={{ marginLeft: '.5rem', fontSize: '.875rem', fontWeight: 'normal' }}
                        >
                          {t.matrixHelpLink}
                          <span className="ods-icon ods-icon--new-window ods-margin-left-1" aria-hidden="true"></span>
                        </a>
                      </label>
                      {age == null && <p className="ods-text--size-mike ods-margin-top-2">{t.eventsHint}</p>}
                      {age != null && eligible.length === 0 && <p className="ods-text--size-mike ods-margin-top-2">{t.noEventsAvailable}</p>}
                      {age != null && eligible.length > 0 && (
                        <div className="ods-margin-top-2">
                          {eligible.map(ev => {
                            const checked = child.EventIds.includes(ev.Id);
                            return (
                              <OKomCheckbox
                                key={`${ev.ReadableId}-${checked}`}
                                checked={checked}
                                onChange={() => toggleEvent(index, ev.Id)}
                              >
                                {language === 'no'
                                  ? `${ev.NameNo} (${ev.NameTa})`
                                  : `${ev.NameTa} (${ev.NameNo})`}
                              </OKomCheckbox>
                            );
                          })}
                        </div>
                      )}
                    </>
                  );
                })()}

                <label className="ods-input__label ods-margin-top-11">{t.department}
                  <div className="ods-select__select">
                    <select value={child.DepartmentId} onChange={e => updateChild(index, 'DepartmentId', parseInt(e.target.value, 10))}>
                      <option value={0}>{t.selectDepartment}</option>
                      {departments.filter(d => !EXCLUDED_DEPARTMENT_IDS.has(d.Id)).map(d => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                    </select>
                  </div>
                </label>
              </div>
            </div>
          ))}

          {form.Children.length < MAX_CHILDREN && (
            <button type="button" onClick={addChild} className="ods-button ods-button--yellow ods-button--fullwidth ods-margin-top-3">{t.addChild}</button>
          )}
        </div>

        <div className="ods-grid__column--12 ods-grid__column--start-4-breakpoint-medium ods-grid__column--end-10-breakpoint-medium ods-margin-top-11">
          {validationError && (
            <div className="ods-margin-bottom-3">
              <PktAlert title={t.validationTitle} skin="warning" compact>
                <span>{validationError}</span>
                {emailExistsFor && (
                  <>
                    {' '}
                    <a href="/login" className="o-kom-link">{t.loginLink}</a>
                  </>
                )}
              </PktAlert>
            </div>
          )}
          <div className="ods-text--align-center ods-margin-bottom-3">
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="o-kom-link">
              {t.privacyLink}
              <span className="ods-icon ods-icon--new-window ods-margin-left-1" aria-hidden="true"></span>
            </a>
          </div>
          <button disabled={isLoading || registrationOpen !== true} className="ods-button ods-button--fullwidth" onClick={handleSubmit}>{t.send}</button>
        </div>

      </div>
    </>
  );
};

export default withRouter(AthleticsRegistrationView);
